/**
 * Student report payload types — the sealed Experience Engine™ object,
 * exactly as persisted in `learner_reports` (audience = 'student').
 * Presentation code renders these verbatim; nothing here is computed
 * client-side.
 */

export type Explainability = {
  value: number | null;
  explanation: string;
  evidence: string[];
  confidence: number;
};

export type IntelRow = {
  intelligence_code: string; intelligence_name: string;
  value: number | null; confidence: number; is_null: boolean;
  computation_hash: string; explainability?: Explainability;
};

export type FeatureRow = {
  feature_key: string; feature_label: string; feature_family: string;
  presentation: boolean; value: number | null; confidence: number;
  is_null: boolean; computation_hash: string; explainability?: Explainability;
};

export type CareerRow = {
  career_id: string; career_name: string; career_cluster: string;
  fit_score: number | null; confidence: number;
  matching_intelligences: string[]; matching_features: string[];
  is_null: boolean; computation_hash: string; explainability?: Explainability;
};

export type RecRow = {
  recommendation_rank: number; career_id: string; career_name: string;
  career_cluster: string; recommendation_score: number; fit_score: number | null;
  confidence: number; tier: "best" | "good" | "explore" | "hidden";
  explanation: string; matching_intelligences: string[];
  matching_features: string[]; computation_hash: string;
  explainability?: Explainability;
};

export type StudentPayload = {
  presentation_snapshot?: {
    template_version: string;
    roadmaps: import("@/features/roadmap/roadmaps").Roadmap[];
    roadmap_state?: import("@/lib/roadmapSync").RoadmapState;
    college_plan?: import("@/lib/collegeMatch").SavedCollegePlan | null;
  };
  saved_copy?: { saved_at: string; run_id: string };
  audience: "student";
  status: { status: "ready" | "not_ready"; missing?: string[] };
  learner: { learner_id: string; display_name?: string | null; age_group?: string | null };
  career_dna: { top_intelligences: IntelRow[]; all: IntelRow[] };
  hdi_summary: IntelRow[];
  career_matches: CareerRow[];
  recommendations: RecRow[];
  growth_opportunities: FeatureRow[];
  future_skills: FeatureRow[];
  provenance: {
    engine_version: string; item_bank_version: string;
    feature_registry_version: string; intelligence_registry_version: string;
    career_dataset_version: string; recommendation_recipe_version: string;
    experience_version: string; experience_template_id: string;
    generated_at: string; canonical: true;
    computation_hashes: Record<string, string | null>;
  };
  report_hash: string;
};

export function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  const n = v <= 1 ? v * 100 : v;
  return `${Math.round(n)}`;
}

export function bar(v: number | null | undefined): number {
  if (v == null || Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(100, v <= 1 ? v * 100 : v));
}
